/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect, useState } from "react";

import {
  Box,
  Button,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Stack,
  Text
} from "@chakra-ui/react";

import { useDispatch, useSelector } from "react-redux";
import ReactSelect from "react-select";

import { IAccountingData } from "@/jenesys-types/jenesys-entities/config";

import {
  loadAccountingShortData,
  selectAccountingShortData
} from "@/store/features/clientSettings/connections/connections";
import { customSelectStyles } from "@/util/helpers";

enum FETCH_STAGES {
  CHOOSE_BUSINESS,
  CANCELLED
}

interface ChooseBusinessProps {
  clientId: string | undefined;
  disclosure: any;
  linkBusiness: any;
  isLinking: any;
  handleDisconnect: any;
}

export const ChooseBusiness: React.FC<ChooseBusinessProps> = ({
  clientId,
  disclosure,
  linkBusiness,
  isLinking,
  handleDisconnect
}) => {
  const dispatch = useDispatch();

  const { isOpen, onClose } = disclosure;
  const [stage, setStage] = useState<FETCH_STAGES>(
    FETCH_STAGES.CHOOSE_BUSINESS
  );
  const [business, setBusiness] = useState<
    IAccountingData["businesses"][0] | null
  >(null);

  const data = useSelector(selectAccountingShortData);

  useEffect(() => {
    if (clientId) {
      dispatch(loadAccountingShortData(clientId));
    }
  }, [dispatch, clientId]);

  const businesses: any = data?.businesses || [];

  const handleSetChosenBusiness = () => {
    linkBusiness({
      business_id: business?.platform_record_id,
      business_name: business?.name,
      company_id: clientId,
      platform: business?.integration.toLowerCase()
    }, onClose);
  };

  const closeAndReset = () => {
    onClose();
    setStage(FETCH_STAGES.CHOOSE_BUSINESS);
    setBusiness(null);
  };

  const cancelConnection = () => {
    handleDisconnect({
      platform: business?.integration.toLowerCase(),
      company_id: clientId
    });
  };

  return (
    <Modal
      isCentered
      closeOnOverlayClick={false}
      closeOnEsc={false}
      isOpen={isOpen}
      onClose={closeAndReset}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent rounded={16}>
        <ModalBody px={12} py={12}>
          {stage === FETCH_STAGES.CANCELLED && (
            <Stack spacing={4}>
              <Heading fontSize="xl" fontWeight={600} color="primary.500">
                Try again later
              </Heading>
              <Text pb={2} fontSize={14} fontWeight="500">
                We understand that now might not be the ideal time for you. If
                you&lsquo;d like to connect to Sage, you can do it later.
              </Text>

              <HStack justify="flex-end" spacing={4} pt={2}>
                <Button size="sm" onClick={closeAndReset} w={204} h={9}>
                  Return to Dashboard
                </Button>
              </HStack>
            </Stack>
          )}

          {stage === FETCH_STAGES.CHOOSE_BUSINESS && (
            <Stack spacing={4}>
              <Heading fontSize="xl" fontWeight={600} color="secondary.600">
                One Last Step
              </Heading>
              <Text pb={2} fontWeight="500">
                Select the company from the platform you want to link to your Jenesys
                Dashboard
              </Text>

              <Box>
                <Text pb={1} fontSize={14} fontWeight="500">
                  Select organisation
                </Text>
                <ReactSelect
                  styles={customSelectStyles("md")}
                  placeholder="Please select organisation..."
                  value={business}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  options={businesses}
                  required
                  onChange={setBusiness}
                />
              </Box>

              <HStack justify="flex-end" spacing={4} pt={2}>
                <Button
                  size="sm"
                  w={93}
                  h={9}
                  variant="outline"
                  onClick={() => {
                    cancelConnection();
                    setStage(FETCH_STAGES.CANCELLED);
                  }}
                >
                  Cancel
                </Button>

                <Button
                  size="sm"
                  isLoading={isLinking}
                  loadingText="Connecting..."
                  onClick={handleSetChosenBusiness}
                  isDisabled={!business}
                  w={157}
                  h={9}
                >
                  Connect
                </Button>
              </HStack>
            </Stack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
